import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  Clock,
  Zap,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  StopCircle
} from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';

interface Task {
  id: string;
  description: string;
  tool?: string;
  status: 'pending' | 'executing' | 'completed' | 'failed' | 'skipped';
  progress?: number;
  result?: any;
  error?: string;
  startedAt?: Date;
  completedAt?: Date;
  parent_step?: number;
  dependencies?: number[];
}

interface ExecutionMonitorProps {
  sessionId: string;
  executionId: string;
  planId: string;
  tasks: Task[];
  onCancel?: () => void;
  onRetry?: (taskId: string) => void;
}

export const ExecutionMonitor: React.FC<ExecutionMonitorProps> = ({
  sessionId: _sessionId,
  executionId: _executionId,
  planId: _planId,
  tasks: initialTasks,
  onCancel,
  onRetry
}) => {
  // Initialize tasks with proper IDs
  const [tasks, setTasks] = useState<Task[]>(() => {
    return initialTasks.map((task, index) => ({
      ...task,
      id: task.id || `task_${index + 1}`,
      status: 'pending' as const
    }));
  });
  
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  
  // Get execution updates from store
  const { 
    isExecuting,
    executionUpdates,
    wsConnected
  } = useChatStore();

  // Process execution updates - simplified approach with dynamic step insertion
  useEffect(() => {
    if (!executionUpdates || executionUpdates.length === 0) {
      // No updates to process
      return;
    }

    // Processing updates

    // Build map of latest status for each task
    const statusMap = new Map<string, any>();
    const newStepsToAdd: Task[] = [];

    executionUpdates.forEach(update => {
      if (update.task_id) {
        statusMap.set(update.task_id, update);

        // Check if this is a new step being added
        if (update.metadata?.is_new_step) {
          const existingTask = tasks.find(t => t.id === update.task_id);
          if (!existingTask) {
            // This is a NEW step - add it to the list
            newStepsToAdd.push({
              id: update.task_id,
              description: update.metadata.description || `Step ${update.task_id}`,
              tool: update.metadata.tool,
              status: 'pending',
              parent_step: update.metadata.parent_step,
              dependencies: update.metadata.dependencies || []
            });
          }
        }
      }
    });

    // Status map built

    // Update existing tasks
    let updatedTasks = tasks.map(task => {
      const update = statusMap.get(task.id);

      if (!update) {
        return task;
      }

      // Updating task

      // Map update status to task status
      let newStatus: Task['status'] = task.status;

      if (update.status === 'started' || update.status === 'starting' || update.status === 'progressing') {
        newStatus = 'executing';
      } else if (update.status === 'completed') {
        newStatus = 'completed';
      } else if (update.status === 'failed' || update.status === 'error') {
        newStatus = 'failed';
      }

      // Only update if something changed (or if there's new result/error data)
      if (newStatus !== task.status || update.result || update.error) {
        return {
          ...task,
          status: newStatus,
          result: update.result || task.result,
          error: update.error || task.error,
          startedAt: newStatus === 'executing' && !task.startedAt ? new Date() : task.startedAt,
          completedAt: (newStatus === 'completed' || newStatus === 'failed') ? new Date() : task.completedAt
        };
      }

      return task;
    });

    // Insert new steps in correct position
    if (newStepsToAdd.length > 0) {
      console.log(`📋 ExecutionMonitor: Adding ${newStepsToAdd.length} new step(s)`);

      newStepsToAdd.forEach(newStep => {
        const parentStep = newStep.parent_step;
        if (parentStep) {
          // Find parent step index
          const parentIndex = updatedTasks.findIndex(t =>
            t.id === parentStep.toString() ||
            parseInt(t.id) === parentStep
          );

          if (parentIndex !== -1) {
            // Insert after parent
            console.log(`✅ Inserting step ${newStep.id} after parent at index ${parentIndex}`);
            updatedTasks.splice(parentIndex + 1, 0, newStep);
          } else {
            // Parent not found, append to end
            console.log(`⚠️ Parent step ${parentStep} not found, appending to end`);
            updatedTasks.push(newStep);
          }
        } else {
          // No parent, append to end
          updatedTasks.push(newStep);
        }
      });
    }

    // Check if anything actually changed
    const hasChanges =
      updatedTasks.some((task, index) => task.status !== tasks[index]?.status) ||
      newStepsToAdd.length > 0 ||
      updatedTasks.length !== tasks.length;

    if (hasChanges) {
      // Updating tasks state
      setTasks(updatedTasks);
    } else {
      // No changes detected
    }

  }, [executionUpdates]); // Only depend on executionUpdates, not tasks

  // Calculate overall progress
  const overallProgress = (() => {
    const completed = tasks.filter(t => 
      t.status === 'completed' || t.status === 'failed' || t.status === 'skipped'
    ).length;
    return tasks.length > 0 ? (completed / tasks.length) * 100 : 0;
  })();

  // Determine execution status
  const executionStatus = (() => {
    const allDone = tasks.every(t => 
      t.status === 'completed' || t.status === 'failed' || t.status === 'skipped'
    );
    
    if (!allDone) return 'running';
    
    const hasFailures = tasks.some(t => t.status === 'failed');
    return hasFailures ? 'failed' : 'completed';
  })();

  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-gray-400" />;
      case 'executing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'skipped':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'pending': return 'default';
      case 'executing': return 'secondary';
      case 'completed': return 'success';
      case 'failed': return 'destructive';
      case 'skipped': return 'outline';
    }
  };

  const formatExecutionTime = (startedAt?: Date, completedAt?: Date) => {
    if (!startedAt || !completedAt) return null;
    const ms = completedAt.getTime() - startedAt.getTime();
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Component rendering

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">Execution Monitor</CardTitle>
            {wsConnected ? (
              <Badge variant="outline" className="text-green-600">
                <Zap className="h-3 w-3 mr-1" />
                Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400">
                <AlertCircle className="h-3 w-3 mr-1" />
                Disconnected
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {executionStatus === 'running' && onCancel && (
              <Button
                size="sm"
                variant="destructive"
                onClick={onCancel}
              >
                <StopCircle className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            )}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Overall Progress</span>
            <span className="font-medium">{Math.round(overallProgress)}%</span>
          </div>
          <Progress value={overallProgress} className="h-2" />
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>
              Completed: {tasks.filter(t => t.status === 'completed').length}
            </span>
            <span>
              Failed: {tasks.filter(t => t.status === 'failed').length}
            </span>
            <span>
              Remaining: {tasks.filter(t => t.status === 'pending').length}
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <ScrollArea className="h-[400px]">
          <div className="space-y-2">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="border rounded-lg p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2 flex-1">
                    <button
                      onClick={() => toggleTaskExpansion(task.id)}
                      className="mt-0.5"
                    >
                      {expandedTasks.has(task.id) ? 
                        <ChevronDown className="h-4 w-4" /> : 
                        <ChevronRight className="h-4 w-4" />
                      }
                    </button>
                    
                    {getStatusIcon(task.status)}
                    
                    <div className="flex-1">
                      <p className="text-sm font-medium">{task.description}</p>
                      
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {task.tool && (
                          <span className="flex items-center gap-1">
                            <Zap className="h-3 w-3" />
                            {task.tool}
                          </span>
                        )}
                        
                        {(task.startedAt && task.completedAt) && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatExecutionTime(task.startedAt, task.completedAt)}
                          </span>
                        )}
                      </div>

                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusColor(task.status) as any}>
                      {task.status}
                    </Badge>
                    
                    {task.status === 'failed' && onRetry && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onRetry(task.id)}
                      >
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedTasks.has(task.id) && (
                  <div className="mt-3 pl-8 space-y-2">
                    {task.error && (
                      <Alert className="border-red-200 bg-red-50">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-sm">
                          {task.error}
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    {task.result && (
                      <div className="bg-muted/50 rounded p-2">
                        <p className="text-xs font-medium mb-1">Result:</p>
                        <pre className="text-xs overflow-x-auto">
                          {typeof task.result === 'string' 
                            ? task.result 
                            : JSON.stringify(task.result, null, 2)
                          }
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Completion Status */}
        {executionStatus !== 'running' && (
          <Alert className="mt-4">
            {executionStatus === 'completed' && (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  Execution completed successfully. All tasks have been processed.
                </AlertDescription>
              </>
            )}
            
            {executionStatus === 'failed' && (
              <>
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription>
                  Execution completed with errors. Some tasks failed to execute.
                </AlertDescription>
              </>
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};